import { Icon } from "@iconify/react";
import { useState } from "react";
import type { ChatAttachment } from "../../api/getAttachments";
import { AttachmentsModal } from "../AttachmentsModal";

interface AttachmentsBarProps {
  attachments: ChatAttachment[];
  chatSessionId: string;
  isDarkMode: boolean;
}

// Helper function to get icon based on mime type
export function getFileIcon(mimeType: ChatAttachment["mime_type"]): string {
  if (mimeType === "application/pdf") {
    return "mdi:file-pdf-box";
  }
  if (
    mimeType === "application/msword" ||
    mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return "mdi:file-word-box";
  }
  if (
    mimeType === "application/vnd.ms-excel" ||
    mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ) {
    return "mdi:file-excel-box";
  }
  if (
    mimeType === "application/vnd.ms-powerpoint" ||
    mimeType === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  ) {
    return "mdi:file-powerpoint-box";
  }
  if (mimeType === "text/plain") {
    return "mdi:file-document-outline";
  }
  if (mimeType === "text/csv") {
    return "mdi:file-delimited-outline";
  }
  if (mimeType === "text/rtf" || mimeType === "application/rtf") {
    return "mdi:file-document-outline";
  }
  if (
    mimeType === "image/jpeg" ||
    mimeType === "image/png" ||
    mimeType === "image/gif" ||
    mimeType === "image/webp"
  ) {
    return "mdi:file-image-outline";
  }
  // Default icon
  return "mdi:file-outline";
}

// Helper function to get color class based on mime type
export function getFileIconColor(
  mimeType: ChatAttachment["mime_type"],
  isDarkMode: boolean,
): string {
  if (mimeType === "application/pdf") {
    return "text-red-500";
  }
  if (
    mimeType === "application/msword" ||
    mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
  ) {
    return "text-blue-500";
  }
  if (
    mimeType === "application/vnd.ms-excel" ||
    mimeType === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  ) {
    return "text-green-600";
  }
  if (
    mimeType === "application/vnd.ms-powerpoint" ||
    mimeType === "application/vnd.openxmlformats-officedocument.presentationml.presentation"
  ) {
    return "text-orange-500";
  }
  if (
    mimeType === "image/jpeg" ||
    mimeType === "image/png" ||
    mimeType === "image/gif" ||
    mimeType === "image/webp"
  ) {
    return "text-purple-500";
  }
  // Default color for text files and others
  return isDarkMode ? "text-gray-400" : "text-gray-600";
}

export function AttachmentsBar({ attachments, chatSessionId, isDarkMode }: AttachmentsBarProps) {
  const [showModal, setShowModal] = useState(false);

  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <>
      <div
        className={[
          "flex",
          "gap-2",
          "overflow-x-auto",
          "px-2",
          "md:px-4",
          "py-2",
          "border-b",
          "min-w-0",
          isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white",
        ].join(" ")}
        style={{ scrollbarWidth: "thin" }}
      >
        {attachments.map((attachment) => (
          <button
            key={attachment.uid}
            type="button"
            onClick={() => setShowModal(true)}
            className={[
              "grid",
              "grid-cols-[auto_1fr]",
              "items-center",
              "gap-2",
              "px-3",
              "py-2",
              "rounded-lg!",
              "min-w-0",
              "flex-shrink-0",
              "transition-colors",
              "cursor-pointer",
              "border-0",
              isDarkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-100 hover:bg-gray-200",
            ].join(" ")}
            title={attachment.filename}
          >
            <Icon
              icon={getFileIcon(attachment.mime_type)}
              className={`w-5 h-5 ${getFileIconColor(attachment.mime_type, isDarkMode)}`}
            />
            <span
              className={[
                "text-sm",
                "truncate",
                "max-w-[200px]",
                "md:max-w-[300px]",
                isDarkMode ? "text-gray-300" : "text-gray-700",
              ].join(" ")}
            >
              {attachment.filename}
            </span>
          </button>
        ))}
      </div>
      {showModal && (
        <AttachmentsModal
          chatSessionId={chatSessionId}
          isDarkMode={isDarkMode}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}
